import { useEffect, useRef, useState } from "react";
import { Plus, Trash2, Lock, Upload, Pencil, X } from "lucide-react";
import api, { formatApiError, fileUrl } from "@/lib/api";

const EMPTY = {
  title: "",
  authors: "",
  year: "",
  doc_type: "ouvrage",
  discipline: "",
  abstract: "",
  keywords: "",
  city: "Kinshasa",
  file_path: "",
  cover_image: "",
  requires_subscription: false,
  published: true,
};

const DOC_TYPES = [
  { value: "ouvrage", label: "Ouvrage" },
  { value: "revue", label: "Revue" },
  { value: "notes_cours", label: "Notes de cours" },
  { value: "essai", label: "Essai" },
  { value: "roman", label: "Roman" },
  { value: "article", label: "Article" },
  { value: "thèse", label: "Thèse" },
  { value: "autre", label: "Autre" },
];

export default function PublicationsAdmin() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState("");
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const fileRef = useRef();
  const coverRef = useRef();

  const load = () => api.get("/publications?limit=200").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const upload = async (file, kind, setter) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", kind);
    setter(true);
    try {
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      return data.path;
    } finally {
      setter(false);
    }
  };

  const onPickFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const path = await upload(f, "publication", setUploadingFile);
      setForm((v) => ({ ...v, file_path: path }));
    } catch (err) { setError(formatApiError(err)); }
  };

  const onPickCover = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const path = await upload(f, "cover", setUploadingCover);
      const url = fileUrl(path);
      setForm((v) => ({ ...v, cover_image: url }));
    } catch (err) { setError(formatApiError(err)); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      if (editingId) {
        await api.patch(`/publications/${editingId}`, form);
      } else {
        await api.post("/publications", form);
      }
      setForm(EMPTY); setEditingId(null);
      if (fileRef.current) fileRef.current.value = "";
      if (coverRef.current) coverRef.current.value = "";
      load();
    } catch (err) { setError(formatApiError(err)); }
  };

  const edit = (p) => { setForm({ ...EMPTY, ...p }); setEditingId(p.id); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const del = async (id) => {
    if (!window.confirm("Supprimer cette publication ?")) return;
    await api.delete(`/publications/${id}`); load();
  };
  const cancel = () => { setForm(EMPTY); setEditingId(null); };

  return (
    <div data-testid="publications-admin">
      <div className="eyebrow eyebrow-dark">Bibliothèque numérique</div>
      <h1 className="mt-2 text-3xl font-bold text-unisic-blue">Publications numériques</h1>
      <p className="mt-2 text-slate-600 text-sm">Ajoutez et gérez les ouvrages, articles et documents à publier dans la Bibliothèque numérique. Activez l&apos;abonnement pour restreindre certains contenus.</p>
      <span className="divider-gold mt-3" />

      <form onSubmit={submit} className="mt-8 paper-card p-6 grid gap-4" data-testid="publication-form">
        <div className="flex items-center justify-between">
          <div className="text-sm font-semibold text-unisic-blue">
            {editingId ? "Modifier la publication" : "Ajouter une publication"}
          </div>
          {editingId && (
            <button type="button" onClick={cancel} className="text-xs text-slate-500 hover:text-unisic-red flex items-center gap-1">
              <X className="w-3 h-3" /> Annuler l&apos;édition
            </button>
          )}
        </div>
        <input required placeholder="Titre" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="border border-slate-300 px-3 py-2.5 rounded-sm text-sm" data-testid="pub-title" />
        <div className="grid md:grid-cols-2 gap-4">
          <input required placeholder="Auteur(s)" value={form.authors} onChange={(e) => setForm({ ...form, authors: e.target.value })} className="border border-slate-300 px-3 py-2.5 rounded-sm text-sm" data-testid="pub-authors" />
          <input required placeholder="Année (ex : 2024)" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} className="border border-slate-300 px-3 py-2.5 rounded-sm text-sm" data-testid="pub-year" />
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <select value={form.doc_type} onChange={(e) => setForm({ ...form, doc_type: e.target.value })} className="border border-slate-300 px-3 py-2.5 rounded-sm text-sm bg-white" data-testid="pub-type">
            {DOC_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
          <input placeholder="Discipline" value={form.discipline} onChange={(e) => setForm({ ...form, discipline: e.target.value })} className="border border-slate-300 px-3 py-2.5 rounded-sm text-sm" />
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <input placeholder="Ville de publication (défaut : Kinshasa)" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="border border-slate-300 px-3 py-2.5 rounded-sm text-sm" data-testid="pub-city" />
          <div></div>
        </div>
        <textarea rows={3} placeholder="Résumé (facultatif)" value={form.abstract} onChange={(e) => setForm({ ...form, abstract: e.target.value })} className="border border-slate-300 px-3 py-2.5 rounded-sm text-sm" />
        <input placeholder="Mots-clés (séparés par virgules)" value={form.keywords} onChange={(e) => setForm({ ...form, keywords: e.target.value })} className="border border-slate-300 px-3 py-2.5 rounded-sm text-sm" />

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-slate-600">Fichier PDF</label>
            <div className="mt-1 flex items-center gap-3">
              <input ref={fileRef} type="file" accept="application/pdf" onChange={onPickFile} className="text-xs" data-testid="pub-file" />
              {uploadingFile && <span className="text-xs text-slate-500">Envoi…</span>}
              {form.file_path && !uploadingFile && <span className="text-xs text-green-700 flex items-center gap-1"><Upload className="w-3 h-3" /> Envoyé</span>}
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-slate-600">Couverture (image)</label>
            <div className="mt-1 flex items-center gap-3">
              <input ref={coverRef} type="file" accept="image/*" onChange={onPickCover} className="text-xs" data-testid="pub-cover" />
              {uploadingCover && <span className="text-xs text-slate-500">Envoi…</span>}
              {form.cover_image && !uploadingCover && (
                <img src={form.cover_image} alt="" className="w-8 h-8 object-cover rounded-sm" />
              )}
            </div>
          </div>
        </div>

        <label className="flex items-center gap-3 text-sm cursor-pointer">
          <input type="checkbox" checked={form.requires_subscription} onChange={(e) => setForm({ ...form, requires_subscription: e.target.checked })} data-testid="pub-subscription-toggle" />
          <Lock className="w-4 h-4 text-unisic-gold" />
          Nécessite un abonnement pour être consulté
        </label>
        <label className="flex items-center gap-3 text-sm cursor-pointer">
          <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} />
          Publier sur la Bibliothèque numérique
        </label>

        {error && <div className="text-sm text-unisic-red" data-testid="pub-error">{error}</div>}
        <div className="flex justify-end">
          <button className="btn-primary text-sm" type="submit" data-testid="btn-save-pub"><Plus className="w-4 h-4" /> {editingId ? "Enregistrer" : "Ajouter"}</button>
        </div>
      </form>

      <div className="mt-10 paper-card overflow-hidden">
        <table className="unisic-table w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-4 py-3">Titre</th>
              <th className="px-4 py-3">Auteur(s)</th>
              <th className="px-4 py-3">Type</th>
              <th className="px-4 py-3">Année</th>
              <th className="px-4 py-3">Abonnement</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id} className="hover:bg-slate-50" data-testid={`pub-row-${p.id}`}>
                <td className="px-4 py-3 max-w-md truncate">{p.title}</td>
                <td className="px-4 py-3">{p.authors}</td>
                <td className="px-4 py-3">{p.doc_type}</td>
                <td className="px-4 py-3">{p.year}</td>
                <td className="px-4 py-3">{p.requires_subscription ? <span className="inline-flex items-center gap-1 text-xs text-unisic-gold font-semibold"><Lock className="w-3 h-3" /> Oui</span> : <span className="text-xs text-slate-400">Libre</span>}</td>
                <td className="px-4 py-3 text-right space-x-2">
                  <button onClick={() => edit(p)} className="text-slate-400 hover:text-unisic-blue" data-testid={`btn-edit-pub-${p.id}`}><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => del(p.id)} className="text-slate-400 hover:text-unisic-red" data-testid={`btn-del-pub-${p.id}`}><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={6} className="text-center py-10 text-slate-500">Aucune publication.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
